/** PayTo agreement limits — live agreements whose subscription runs above its own cap / weekly count (Dean, 23 Oct 2026).
 *  The customer's bank rejects those payments (GoCardless: failed, "reason not provided"); the only fixes are a new
 *  agreement (customer re-authorises) or BECS Direct Debit. */
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, ExternalLink, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { loadPaytoOverCap } from "@/lib/api";
import { fmtAUD } from "./LoanRepaymentsAusParts";

export function PaytoLimitsSheet({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (refresh = false) => {
    setBusy(true);
    try { setData(await loadPaytoOverCap(refresh)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the PayTo limits report"); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open) load(false); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.over_cap || [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[720px] sm:max-w-[720px] overflow-y-auto" data-testid="payto-limits-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-violet-700" />PayTo agreement limits</SheetTitle>
          <SheetDescription>
            Live PayTo agreements where what we collect is above what the customer authorised. The bank rejects those payments —
            re-issue the agreement with the right cap (customer re-authorises) or move the customer to BECS Direct Debit.
          </SheetDescription>
        </SheetHeader>
        <div className="flex items-center justify-between mt-4 mb-2">
          <p className="text-xs text-muted-foreground" data-testid="payto-limits-meta">
            {data ? <>{data.scanned} active PayTo agreements scanned · <b className={rows.length ? "text-red-700" : "text-emerald-700"}>{rows.length} above their limits</b> · {data.generated_at?.slice(0, 16).replace("T", " ")} UTC</> : "Loading…"}
          </p>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => load(true)} disabled={busy} data-testid="payto-limits-refresh">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Re-scan GoCardless
          </Button>
        </div>
        {data && rows.length === 0 && <p className="text-sm text-emerald-700" data-testid="payto-limits-clear">Every live PayTo agreement is within its limits.</p>}
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.mandate_id} className="border rounded-lg p-3 text-sm bg-white" data-testid={`payto-over-cap-${r.mandate_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{r.sfa_number || "—"} · {r.customer || "unknown customer"}</p>
                  <p className="text-[11px] text-muted-foreground font-mono">{r.mandate_id} · agreed {r.created_at}</p>
                </div>
                <div className="flex items-center gap-2">
                  {r.over_by > 0 && <Badge className="bg-red-600 text-[10px]">+{fmtAUD(r.over_by)} per payment</Badge>}
                  <a href={r.dashboard_url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 inline-flex items-center gap-1">GoCardless <ExternalLink className="w-3 h-3" /></a>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                <div><span className="text-muted-foreground">Agreement cap</span><br /><b>{fmtAUD(r.cap)}</b> / payment · {r.max_per_week ?? "—"} a week</div>
                <div><span className="text-muted-foreground">We collect</span><br /><b className="text-red-700">{fmtAUD(r.subscription_amount)}</b> · {r.per_week} a week</div>
                <div><span className="text-muted-foreground">Fix</span><br />{r.action}</div>
              </div>
              {r.problems?.map((p, i) => <p key={i} className="text-[11px] text-red-700 mt-1"><AlertTriangle className="w-3 h-3 inline mr-1" />{p}</p>)}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
